import { capitalize } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { ApiOrgProduct, getScanInlineProductFromOrgProduct } from '@kaiizen/kai-pro-api';
import {
  getEffectiveScanValue,
  getTotalCostIfDefined,
  getUpdatedScanValue,
  isLineItemScoped,
  ScanLineItem,
  ScanLineItemOptimized,
  ScanTemplateLineItem,
  ScanTemplateOption,
  ScanTemplateSkuOption,
  ScanTemplateValuePresenterNames,
  ScanValue,
} from '@kaiizen/kai-scan-template-system';
import React, { useCallback, useRef, useState } from 'react';

import { formatUsd, getLineItemModifiers } from '@/Client/Presenters';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { CountInput, ModifierTag, ProductThumbnail } from '@/Client/Widgets';
import { isNumber, isString } from '@art-suite/art-core-ts';
import { ScanDataMutators, useOrgProduct, useScanEditingContextSlice } from '../../State';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import { CombinedProductSearchModal } from '../ScanTemplateEditor/CombinedProductSearchModal';
import { ApproveCatalogPriceChange, shouldShowApproveCatalogPriceChange } from './ApproveCatalogPriceChange';
import { LineItemStatusMenu } from './LineItemStatusMenu';
import { OrgProductFlyout } from './OrgProductFlyout';
import { ScanValueView } from './ScanValueView';

const ROW_HEIGHT = LAYOUT2.spacing6;

const LINE_ITEM_STYLE = css`
  label: LINE_ITEM_STYLE;
  display: flex;
  flex-direction: column;
`;

const LINE_ITEM_DNE_YET_STYLE = css`
  label: LINE_ITEM_DNE_YET_STYLE;
  opacity: 1; /* set to 0.5 for helpful debugging so you can see the line item doesn't exist yet */
`;

const LINE_ITEM_HEADER_STYLE = css`
  label: LINE_ITEM_HEADER_STYLE;
  display: flex;
  align-items: flex-start;
  min-height: ${ROW_HEIGHT}px;
  background: var(--background);
  padding-left: ${LAYOUT2.spacing1}px;
`;

const LINE_ITEM_TITLE_STYLE = css`
  label: LINE_ITEM_TITLE_STYLE;
  padding: 0 0 ${LAYOUT2.spacing1p5}px 0;
  display: flex;
  align-items: flex-start;
  ${TYPOGRAPHY2.xSmallBold}
  color: var(--text);
  gap: ${LAYOUT2.spacing1}px;
  flex: 1;
  min-width: 0;
  padding-right: ${LAYOUT2.spacing1}px;
`;

const LINE_TITLE_OVERFLOW_CONTAINER_STYLE = css`
  label: LINE_TITLE_OVERFLOW_CONTAINER_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
  flex: 1;
  min-width: 0;
`;

const MATERIAL_LINE_ITEM_AMOUNT_SCOPED_STYLE = css`
  label: MATERIAL_LINE_ITEM_AMOUNT_SCOPED_STYLE;
  ${TYPOGRAPHY2.mediumBold}
  color: var(--textGreen2);
`;

const LINE_ITEM_CLICKABLE_STYLE = css`
  label: LINE_ITEM_CLICKABLE_STYLE;
  cursor: pointer;
  margin-right: ${LAYOUT2.spacingHalf}px;
  padding-top: ${LAYOUT2.spacingHalf}px;
  margin-top: -${LAYOUT2.spacingHalf}px;
  padding-left: ${LAYOUT2.spacingHalf}px;
  margin-left: -${LAYOUT2.spacingHalf}px;

  &:hover {
    background-color: ${COLORS2.alwaysDark.lightestGray};
  }
`;

const LINE_ITEM_TITLE_NAME_STYLE = css`
  label: LINE_ITEM_TITLE_NAME_STYLE;
  ${TYPOGRAPHY2.xSmallBold}
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  line-height: 1.4;
  max-height: calc(1.4em * 1);
  overflow: hidden;
  display: -webkit-box;
  color: var(--text);
`;

const LINE_ITEM_TITLE_DESCRIPTION_STYLE = css`
  label: LINE_ITEM_TITLE_DESCRIPTION_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  line-height: 1.4;
  max-height: calc(1.4em * 3);
`;

const LINE_ITEM_TITLE_SKU_STYLE = css`
  label: LINE_ITEM_TITLE_SKU_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
  overflow: hidden;
  display: -webkit-box;
`;

/* The tags carry their own shape, so the row around them is only a row: wrapping them in the
   old single-badge box put every grey tag inside a tan pill and ran them together. */
const MODIFIER_TAG_ROW_STYLE = css`
  label: MODIFIER_TAG_ROW_STYLE;
  display: flex;
  flex-wrap: wrap;
  gap: ${LAYOUT2.spacingHalf}px;
  justify-content: flex-end;
`;

const LABEL_WRAPPER_STYLE = css`
  label: LABEL_WRAPPER_STYLE;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const LABEL_WRAPPER_LABEL_STYLE = css`
  label: LABEL_WRAPPER_LABEL_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
  height: ${pxToRem(TYPOGRAPHY2.fontSize.xSmall)};
  line-height: 1; // matches this block's font-size (xSmall) exactly
`;

const LABEL_WRAPPER_CONTENT_STYLE = css`
  label: LABEL_WRAPPER_CONTENT_STYLE;
  flex: 1;
  display: flex;
  align-items: center;
`;

const INPUTS_COLUMN_STYLE = css`
  label: NUMBERS_COLUMN_STYLE;
  min-width: 0;
  flex: 0 1 auto;
  width: 140px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
  padding: 0 0 ${LAYOUT2.spacing1}px 0;
`;

const TOTAL_COLUMN_STYLE = css`
  label: TOTAL_COLUMN_STYLE;
  width: 120px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
  padding: 0 0 ${LAYOUT2.spacing1}px 0;
`;

const MENU_COLUMN_STYLE = css`
  label: MENU_COLUMN_STYLE;
  width: 45px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
`;

const TOTAL_AMOUNT_STYLE = css`
  label: TOTAL_AMOUNT_STYLE;
  height: 60px;
  padding-top: 11px;
  ${TYPOGRAPHY2.medium}
  color: var(--text);
  text-align: right;
  width: 100%;
`;

const PRICE_ROW_STYLE = css`
  label: PRICE_ROW_STYLE;
  display: flex;
  flex-direction: row;
  gap: 5px;
  width: 100%;
  min-width: 0;
`;

const PRICE_FIELD_FLEX_STYLE = css`
  label: PRICE_FIELD_FLEX_STYLE;
  flex: 1 1 0;
  min-width: 0;
`;

const LabelWrapper = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div css={LABEL_WRAPPER_STYLE}>
    <div css={LABEL_WRAPPER_LABEL_STYLE}>{label}</div>
    <div css={LABEL_WRAPPER_CONTENT_STYLE}>{children}</div>
  </div>
);

const getSkuIdStringFromSkuIdOptions = (skuIdOption?: ScanTemplateSkuOption | undefined): string | undefined => {
  if (!skuIdOption) return undefined;
  return isString(skuIdOption) ? skuIdOption : skuIdOption.id;
};

export const InlineLineItemEditor = React.memo(({
  lineItemTemplate,
  optionTemplate,
  lineItem,
  taskId,
  groupId,
  optionId,
  scanDataMutators,
}: {
  lineItemTemplate: ScanTemplateLineItem;
  optionTemplate: ScanTemplateOption;
  lineItem?: ScanLineItemOptimized;
  taskId: string;
  groupId: string;
  optionId?: string;
  scanDataMutators: ScanDataMutators;
}) => {
  const { scanIsEditable, enabledTaskModifiers, hasEnabledTaskModifiers, knownTaskModifiers } = useScanEditingContextSlice([
    'scanIsEditable',
    'enabledTaskModifiers',
    'hasEnabledTaskModifiers',
    'knownTaskModifiers',
  ]);
  const lineItemRef = useRef<HTMLDivElement>(null);
  const totalCost = getTotalCostIfDefined(lineItem);
  const isScoped = isLineItemScoped(lineItem);
  const isScopeStagePickable = !!lineItemTemplate.useScopeStageSkus;
  const hasProducts = (lineItemTemplate.skuIdOptions?.length ?? 0) > 0 || isScopeStagePickable;

  const skuId = lineItem?.skuId ?? getSkuIdStringFromSkuIdOptions(lineItemTemplate.skuIdOptions?.[0]);

  const { orgProduct } = useOrgProduct(skuId);

  const [productFlyoutOpen, setProductFlyoutOpen] = useState(false);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const canPickProduct = isScopeStagePickable && scanIsEditable;

  const updateValue = useCallback((key: string, newValue: ScanValue) => {
    if (!scanIsEditable) return;

    const updates: Partial<ScanLineItem> = { values: { [key]: newValue } };
    if (!optionId) {
      scanDataMutators.addOption({
        groupId,
        taskId,
        optionTemplateId: optionTemplate.id,
        addLineItemTemplateId: lineItemTemplate.id,
        addLineItemInitializers: updates,
      });
    } else if (!lineItem) scanDataMutators.addLineItem({ groupId, taskId, optionId, lineItemTemplateId: lineItemTemplate.id, initializers: updates });
    else scanDataMutators.updateLineItem(groupId, taskId, optionId, lineItem.id, updates);
  }, [lineItem, groupId, taskId, optionId, lineItemTemplate.id, optionTemplate.id, scanDataMutators, scanIsEditable]);

  const isDeferred = lineItem?.tags?.deferred === true;
  const isTenantResponsibility = lineItem?.tags?.tenantResponsibility === true;
  /* One tag per modifier the line item carries, named by the org — this was two checks against
     fixed tags, so an org-created modifier never reached the row (KAIDEV-4206). Read from the
     known list, not the enabled one: disabling a modifier must not blank the rows it tagged. */
  const lineItemModifiers = getLineItemModifiers(lineItem?.tags, knownTaskModifiers);

  const quantityLabel = capitalize(
    lineItemTemplate.values?.quantity?.type === 'count' ? 'Quantity' : ScanTemplateValuePresenterNames[lineItemTemplate.values?.quantity?.type || ''] || '',
  );

  const lineItemHasLabor = !!lineItemTemplate.values?.laborCost;
  const lineItemHasMaterial = !!lineItemTemplate.values?.materialCost;
  const lineItemHasPrice = !!lineItemTemplate.values?.price;
  const lineItemHasLaborPrice = !!lineItemTemplate.values?.laborPrice;
  const lineItemHasQuantity = !!lineItemTemplate.values?.quantity;

  const selectProduct = useCallback((selectedOrgProduct: ApiOrgProduct) => {
    if (!scanIsEditable) return;
    const updates: Partial<ScanLineItem> = {
      skuId: selectedOrgProduct.id,
      skuDetails: getScanInlineProductFromOrgProduct(selectedOrgProduct),
      ...(lineItemHasPrice && isNumber(selectedOrgProduct.price) ? { values: { price: { setValue: selectedOrgProduct.price } } } : {}),
    };
    // not sure why we are gate-keeping so many possilbe states here
    // but I guess this is a reasonable safeguard given that we migth be updating a lineItem
    // after it initially exists
    if (!optionId) {
      scanDataMutators.addOption({
        groupId,
        taskId,
        optionTemplateId: optionTemplate.id,
        addLineItemTemplateId: lineItemTemplate.id,
        addLineItemInitializers: updates,
      });
    } else if (!lineItem) scanDataMutators.addLineItem({ groupId, taskId, optionId, lineItemTemplateId: lineItemTemplate.id, initializers: updates });
    else scanDataMutators.updateLineItem(groupId, taskId, optionId, lineItem.id, updates);
    setProductSearchOpen(false);
  }, [scanIsEditable, lineItemHasPrice, optionId, lineItem, groupId, taskId, optionTemplate.id, lineItemTemplate.id, scanDataMutators, setProductSearchOpen]);

  const lineItemId = lineItem?.id;
  const lineItemQuantity = lineItem?.values?.quantity;
  const lineItemPrice = lineItem?.values?.price;
  const lineItemPriceTemplateValue = lineItemTemplate.values?.price;
  const lineItemLaborPrice = lineItem?.values?.laborPrice;

  const defaultPrice = orgProduct?.price;
  const priceIsLive = !!orgProduct?.productId; // products from the Product Catalog are live

  const showBothPrices = lineItemHasLabor && lineItemHasMaterial;

  const showApproveCatalogPriceChange = shouldShowApproveCatalogPriceChange({
    scanIsEditable,
    lineItemHasPrice,
    priceIsLive,
    defaultPrice,
    lineItemId,
    optionId,
    lineItemPrice,
  });

  const approveCatalogPriceChange = useCallback(() => {
    if (!lineItemId || !optionId || !isNumber(defaultPrice) || !lineItemPriceTemplateValue) return;
    const orgProductForSku = orgProduct;
    if (!orgProductForSku) return;
    scanDataMutators.updateLineItem(groupId, taskId, optionId, lineItemId, {
      skuDetails: getScanInlineProductFromOrgProduct(orgProductForSku),
      values: {
        price: getUpdatedScanValue(lineItemPriceTemplateValue, lineItemPrice, defaultPrice),
      },
    });
  }, [
    defaultPrice,
    groupId,
    lineItemId,
    lineItemPrice,
    lineItemPriceTemplateValue,
    optionId,
    orgProduct,
    scanDataMutators,
    taskId,
  ]);

  const PartPriceEditor = lineItemHasPrice
    ? (
      <ScanValueView
        customLabel='Parts'
        value={lineItemPrice!}
        templateValue={lineItemTemplate.values?.price!}
        valueKey='price'
        updateValue={updateValue}
        size={showBothPrices ? 'tightSmall' : 'small'}
        width='100%'
        backgroundColor='var(--background)'
        disabled={!lineItemPrice || priceIsLive}
        placeholderValue={defaultPrice}
      />
    )
    : undefined;

  const LaborPriceEditor = lineItemHasLaborPrice
    ? (
      <ScanValueView
        customLabel='Labor'
        value={lineItemLaborPrice!}
        templateValue={lineItemTemplate.values?.laborPrice!}
        valueKey='laborPrice'
        updateValue={updateValue}
        size={showBothPrices ? 'tightSmall' : 'small'}
        width='100%'
        backgroundColor='var(--background)'
        disabled={!lineItemLaborPrice}
      />
    )
    : undefined;

  return (
    <div css={[LINE_ITEM_STYLE, !lineItem && LINE_ITEM_DNE_YET_STYLE]} ref={lineItemRef}>
      <div css={LINE_ITEM_HEADER_STYLE}>
        <div
          css={[LINE_ITEM_TITLE_STYLE, (canPickProduct || orgProduct) && LINE_ITEM_CLICKABLE_STYLE]}
          onClick={canPickProduct
            ? e => {
              e.stopPropagation();
              setProductSearchOpen(true);
            }
            : orgProduct
            ? e => {
              e.stopPropagation();
              setProductFlyoutOpen(true);
            }
            : undefined}
        >
          {hasProducts && (
            <ProductThumbnail
              uri={orgProduct?.image}
              photoCount={orgProduct?.image ? 1 : 0}
              onClick={() => {}}
            />
          )}
          <div css={LINE_TITLE_OVERFLOW_CONTAINER_STYLE}>
            <div css={LINE_ITEM_TITLE_NAME_STYLE}>{orgProduct?.title || lineItemTemplate.name}</div>
            {canPickProduct && !orgProduct && <div css={LINE_ITEM_TITLE_DESCRIPTION_STYLE}>Tap to choose a product</div>}
            {orgProduct?.description && <div css={LINE_ITEM_TITLE_DESCRIPTION_STYLE}>{orgProduct?.description}</div>}
            {orgProduct?.thdStoreSku && <div css={LINE_ITEM_TITLE_SKU_STYLE}>{`SKU:  ${orgProduct?.thdStoreSku}`}</div>}
          </div>
        </div>
        <div css={INPUTS_COLUMN_STYLE}>
          {lineItemHasQuantity && (
            <LabelWrapper label={quantityLabel}>
              <CountInput
                value={!lineItemQuantity
                  ? 0
                  : isNumber(getEffectiveScanValue(lineItemQuantity))
                  ? getEffectiveScanValue(lineItemQuantity) as number
                  : undefined}
                disabled={!scanIsEditable}
                onUpdate={quantity => updateValue('quantity', { setValue: quantity })}
              />
            </LabelWrapper>
          )}
          <ApproveCatalogPriceChange
            visible={showApproveCatalogPriceChange}
            lineItemPrice={lineItemPrice}
            catalogUsd={isNumber(defaultPrice) ? defaultPrice : 0}
            onApprove={approveCatalogPriceChange}
          />
          <div css={PRICE_ROW_STYLE}>
            {PartPriceEditor != null && <div css={PRICE_FIELD_FLEX_STYLE}>{PartPriceEditor}</div>}
            {LaborPriceEditor != null && <div css={PRICE_FIELD_FLEX_STYLE}>{LaborPriceEditor}</div>}
          </div>
        </div>
        <div css={TOTAL_COLUMN_STYLE}>
          <div css={[TOTAL_AMOUNT_STYLE, isScoped && MATERIAL_LINE_ITEM_AMOUNT_SCOPED_STYLE]}>
            {formatUsd(totalCost)}
          </div>
          {
            /* "On the product row, directly below the item's price" (KAIDEV-4206). These had sat
              in the product-name block, which reads as part of the product rather than as
              something applied to the line. */
          }
          {lineItemModifiers.length > 0 && (
            <div css={MODIFIER_TAG_ROW_STYLE}>
              {lineItemModifiers.map(({ lineItemTag, displayName }) => <ModifierTag key={lineItemTag} label={displayName} />)}
            </div>
          )}
        </div>
        <div css={MENU_COLUMN_STYLE}>
          {hasEnabledTaskModifiers && optionId && lineItem && (
            <LineItemStatusMenu
              lineItem={lineItem}
              groupId={groupId}
              taskId={taskId}
              optionId={optionId}
              scanDataMutators={scanDataMutators}
              enabledTaskModifiers={enabledTaskModifiers}
              isEditable={scanIsEditable}
            />
          )}
        </div>
      </div>
      <OrgProductFlyout
        orgProduct={orgProduct || null}
        open={productFlyoutOpen}
        onClose={() => setProductFlyoutOpen(false)}
      />
      {canPickProduct && (
        <CombinedProductSearchModal
          isOpen={productSearchOpen}
          onClose={() => setProductSearchOpen(false)}
          onAdd={selectProduct}
        />
      )}
    </div>
  );
});
